import { useState, type FormEvent, type ReactNode } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import type { AdminSessionState, AdminSetupPreview, AdminSetupStart } from '@hoopify/shared';
import { api, errorMessage, fieldErrors } from '../../lib/api';
import { useAdminSession, useSetAdminSession } from '../../lib/admin';
import { Wordmark } from '../../components/Wordmark';
import { NewPasswordField } from '../../components/PasswordField';
import { Alert, Button, Field, LoadingBlock } from '../../components/ui';

export function AdminBadge({ onDark }: { onDark?: boolean }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-semibold tracking-wide uppercase ${onDark ? 'bg-[#00D1FF] text-[#0B0B14]' : 'bg-blue text-brand-ink'}`}>
      <ShieldCheck className="size-3.5" aria-hidden /> Admin
    </span>
  );
}

function AdminAuthLayout({ title, description, children, footer }: { title: string; description?: ReactNode; children: ReactNode; footer?: ReactNode }) {
  return (
    <div className="flex min-h-dvh flex-col px-4 py-8 sm:py-14">
      <div className="mx-auto w-full max-w-[400px]">
        <div className="flex items-center gap-2">
          <Wordmark />
          <AdminBadge />
        </div>
        <h1 className="mt-10 text-[24px] leading-tight sm:text-[28px]">{title}</h1>
        {description && <p className="mt-2 text-muted">{description}</p>}
        <div className="mt-7">{children}</div>
        {footer && <div className="mt-8 border-t border-line pt-5 text-[14px] text-muted">{footer}</div>}
      </div>
    </div>
  );
}

function CodeField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <Field
      label="Code"
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
      inputMode="numeric"
      autoComplete="one-time-code"
      placeholder="123456"
      className="field-input tnum tracking-[0.2em]"
      autoFocus
      required
    />
  );
}

export function AdminLoginPage() {
  const { data, isLoading } = useAdminSession();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const setSession = useSetAdminSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const next = params.get('next')?.startsWith('/admin') ? params.get('next')! : '/admin';

  const login = useMutation({
    mutationFn: () => api.post<AdminSessionState>('/admin/login', { email, password }),
    onSuccess: (s) => {
      setPassword('');
      setSession(s);
    },
  });
  const verify = useMutation({
    mutationFn: () => api.post<AdminSessionState>('/admin/login/code', { code }),
    onSuccess: (s) => {
      setSession(s);
      navigate(next, { replace: true });
    },
    onError: () => setCode(''),
  });
  const cancel = useMutation({ mutationFn: () => api.post<AdminSessionState>('/admin/logout'), onSuccess: (s) => setSession(s) });
  const claim = useQuery({ queryKey: ['admin-claim'], queryFn: () => api.get<{ open: boolean }>('/admin/claim') });

  if (isLoading || claim.isLoading) return <LoadingBlock />;
  if (data?.status === 'signed_in') return <Navigate to={next} replace />;
  if (claim.data?.open && data?.status !== 'mfa_required') return <ClaimForm />;

  if (data?.status === 'mfa_required') {
    return (
      <AdminAuthLayout
        title="Two-step sign-in"
        description="Enter the 6-digit code from your authenticator app."
        footer={
          <button className="hover:text-ink hover:underline" onClick={() => cancel.mutate()}>
            Cancel
          </button>
        }
      >
        <form onSubmit={(e) => { e.preventDefault(); verify.mutate(); }} className="space-y-4" noValidate>
          {verify.isError && <Alert>{errorMessage(verify.error)}</Alert>}
          <CodeField value={code} onChange={setCode} />
          <Button type="submit" loading={verify.isPending} disabled={code.length !== 6} className="btn-primary btn-lg w-full">
            Continue
          </Button>
        </form>
      </AdminAuthLayout>
    );
  }

  const fields = fieldErrors(login.error);
  return (
    <AdminAuthLayout
      title="Admin sign-in"
      description="For Hoopify staff only. Coach accounts don’t work here."
      footer="Lost your phone or password? Ask another admin to reset your sign-in, or run hoopify reset-admin on the server."
    >
      {params.get('expired') && !login.isError && (
        <div className="mb-4">
          <Alert tone="info">You were signed out after 30 minutes without activity.</Alert>
        </div>
      )}
      <form onSubmit={(e: FormEvent) => { e.preventDefault(); login.mutate(); }} className="space-y-4" noValidate>
        {login.isError && !Object.keys(fields).length && <Alert>{errorMessage(login.error)}</Alert>}
        <Field label="Email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} error={fields.email} required autoFocus />
        <Field label="Password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} error={fields.password} required />
        <Button type="submit" loading={login.isPending} className="btn-primary btn-lg w-full">
          Sign in
        </Button>
      </form>
    </AdminAuthLayout>
  );
}

export function AdminSetupPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const setSession = useSetAdminSession();
  const preview = useQuery({ queryKey: ['admin-setup', token], queryFn: () => api.get<AdminSetupPreview>(`/admin/setup?token=${encodeURIComponent(token)}`), retry: false });
  const [name, setName] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');

  const start = useMutation({ mutationFn: () => api.post<AdminSetupStart>('/admin/setup/start', { token, name: name ?? preview.data?.name ?? '', password }) });
  const finish = useMutation({
    mutationFn: () => api.post<AdminSessionState>('/admin/setup/finish', { token, code }),
    onSuccess: (s) => {
      setSession(s);
      navigate('/admin', { replace: true });
    },
    onError: () => setCode(''),
  });

  if (preview.isLoading) return <LoadingBlock />;
  if (preview.isError || !preview.data) {
    return (
      <AdminAuthLayout title="This link doesn’t work">
        <Alert>{errorMessage(preview.error)}</Alert>
      </AdminAuthLayout>
    );
  }
  const p = preview.data;

  if (start.data) {
    const qr = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(start.data.qrSvg)}`;
    return (
      <AdminAuthLayout title="Set up two-step sign-in" description="Admins always use two-step sign-in. Scan this with an authenticator app (such as Google Authenticator, Microsoft Authenticator or 1Password).">
        <img src={qr} alt="QR code to scan with your authenticator app" className="size-48 rounded-md border border-line bg-white p-2" />
        <p className="mt-3 text-[14px] text-muted">Can’t scan it? Enter this setup key in your app:</p>
        <code className="tnum mt-2 block rounded-md bg-sunken px-3 py-2 text-[14px] break-all select-all">{start.data.secret.match(/.{1,4}/g)?.join(' ')}</code>
        <form onSubmit={(e) => { e.preventDefault(); finish.mutate(); }} className="mt-6 space-y-4" noValidate>
          {finish.isError && <Alert>{errorMessage(finish.error)}</Alert>}
          <CodeField value={code} onChange={setCode} />
          <Button type="submit" loading={finish.isPending} disabled={code.length !== 6} className="btn-primary btn-lg w-full">
            Finish setup
          </Button>
        </form>
      </AdminAuthLayout>
    );
  }

  const fields = fieldErrors(start.error);
  const shownName = name ?? p.name;
  return (
    <AdminAuthLayout title="Set up your admin sign-in" description={<>For <span className="font-medium text-ink">{p.email}</span>. Choose a password, then set up two-step sign-in.</>}>
      <form onSubmit={(e) => { e.preventDefault(); start.mutate(); }} className="space-y-4" noValidate>
        {start.isError && !Object.keys(fields).length && <Alert>{errorMessage(start.error)}</Alert>}
        <Field label="Your name" value={shownName} onChange={(e) => setName(e.target.value)} error={fields.name} autoComplete="name" required />
        <NewPasswordField value={password} onChange={setPassword} error={fields.password} userInputs={[p.email, shownName]} />
        <Button type="submit" loading={start.isPending} className="btn-primary btn-lg w-full">
          Next
        </Button>
      </form>
    </AdminAuthLayout>
  );
}

/** A brand-new server: become its first admin with the code shown at the end of setup. */
function ClaimForm() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const claim = useMutation({
    mutationFn: () => api.post<{ token: string }>('/admin/claim', { code, name, email }),
    onSuccess: (r) => navigate(`/admin/setup?token=${encodeURIComponent(r.token)}`, { replace: true }),
  });
  const fields = fieldErrors(claim.error);
  return (
    <AdminAuthLayout
      title="Claim this Hoopify"
      description="Enter the claim code shown at the end of the server setup to become this Hoopify’s admin."
      footer="Lost the code, or it expired? In the Vultr console, run: hoopify-server claim-code"
    >
      <form onSubmit={(e: FormEvent) => { e.preventDefault(); claim.mutate(); }} className="space-y-4" noValidate>
        {claim.isError && !Object.keys(fields).length && <Alert>{errorMessage(claim.error)}</Alert>}
        <Field
          label="Claim code"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          error={fields.code}
          placeholder="K7QM-4XPD-2H"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          className="field-input font-mono tracking-[0.12em]"
          autoFocus
        />
        <Field label="Your name" value={name} onChange={(e) => setName(e.target.value)} error={fields.name} autoComplete="name" />
        <Field label="Your email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} error={fields.email} autoComplete="email" hint="You’ll sign in to the admin console with this." />
        <Button type="submit" loading={claim.isPending} className="btn-primary btn-lg w-full">
          Claim
        </Button>
      </form>
    </AdminAuthLayout>
  );
}
